/**
 * MinimalTile — the floor of what a valid Helix 2000 level looks like.
 *
 * Literally one tile, one spawn point, nothing else. Every field in the
 * schema is here so a future author can see exactly what's required and
 * what's optional by inspection.
 *
 * Canvas is 20×15 tiles (640×480px) — small enough to render entirely
 * in a 1280×720 viewport so the single tile is visually obvious.
 *
 * Coordinate sanity (see docs/guides/LEVEL_FORMATS_EXPLAINED.md):
 *   - Single tile lives at tile coord (10, 12) = pixel (320, 384)
 *     → top-left of the tile is pixel (320, 384); covers (320–352, 384–416).
 *   - Player (40×40 sprite) spawns at tile (10, 10) = pixel (320, 320).
 *     Player.y is the TOP of the hitbox, not the feet — so top at 320
 *     means feet at 360. With gravity (1000 px/s²), player falls the
 *     remaining 24px onto the tile top at 384.
 *   - Ghost character is the default player sprite; no separate entity.
 *
 * If you're making a new map: copy this file, change MAP_ID + MAP_NAME,
 * paint into the layers via the editor (press Backquote), add entities
 * or platforms as needed. This is the canonical new-map reference —
 * the minimum working example instead of a wall of
 * commented-out stubs.
 *
 * Registered in src/game/systems/map/LevelRegistry.ts and wired as the
 * DevConfig.defaultMap fallback, so opening `localhost:3001/` with no
 * preset lands here.
 */

import { PlatformerLevel } from '../../shared/CharacterTypes';
import { GridLevel, LayerType, TileType } from '../../shared/TileEditorTypes';

const MAP_ID = 'minimal-tile';
const MAP_NAME = 'Minimal Tile';
const VERSION = '1.0';

const T = 32;          // Tile size in pixels
const W = 20;          // Width in tiles  (20 × 32 = 640px)
const H = 15;          // Height in tiles (15 × 32 = 480px)

// The single tile — placed centrally, in the GROUND layer, as FLOOR type.
const TILE_COL = 10;
const TILE_ROW = 12;

// Spawn sits 2 tiles above the tile so the player falls briefly and lands
// on top. Keep them in the same column so there's no horizontal drift.
const SPAWN_COL = 10;
const SPAWN_ROW = 10;

// Sparse tile storage — only populate what we actually need.
// Key format: `${x},${y}`; value is TileType enum.
const groundLayer = new Map<string, number>([
  [`${TILE_COL},${TILE_ROW}`, TileType.FLOOR],
]);
const collisionLayer = new Map<string, number>();
const objectsLayer = new Map<string, number>();
const entitiesLayer = new Map<string, number>();
const triggersLayer = new Map<string, number>();

// One runtime platform matches the single tile exactly. Explicit here
// (rather than relying on tile→platform auto-generation) so collision
// geometry is auditable at a glance. A 32×32 block is all we need.
const runtimePlatforms = [
  {
    id: 'only-tile',
    x: TILE_COL * T,          // 320
    y: TILE_ROW * T,          // 384
    width: T,                 // 32
    height: T,                // 32
    type: 'normal' as const,
    color: '#8B7355',         // Floor brown, matches TileType.FLOOR palette
  },
];

export const MinimalTile: GridLevel & Partial<PlatformerLevel> = {
  // ─── Core metadata ──────────────────────────────────────
  id: MAP_ID,
  name: MAP_NAME,
  version: VERSION,

  // ─── Dimensions ─────────────────────────────────────────
  widthTiles: W,
  heightTiles: H,
  width: W * T,
  height: H * T,
  tileSize: T,

  // ─── Visuals + physics ──────────────────────────────────
  backgroundColor: '#1a1a2e',   // Dark blue-purple, matches MAP_DEFAULTS
  gravity: 1000,

  // ─── Spawning ───────────────────────────────────────────
  spawnPoints: [{ x: SPAWN_COL, y: SPAWN_ROW }],  // tile coords
  defaultSpawn: {
    x: SPAWN_COL * T,  // 320 px
    y: SPAWN_ROW * T,  // 320 px
  },

  // ─── Layers (sparse) ────────────────────────────────────
  // All 5 layers are declared so the editor can paint into any of them
  // without having to add the layer structure first. Only GROUND has
  // the one tile; everything else is empty.
  layers: [
    { name: LayerType.GROUND,    visible: true, locked: false, opacity: 1.0, tiles: groundLayer },
    { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: collisionLayer },
    { name: LayerType.OBJECTS,   visible: true, locked: false, opacity: 1.0, tiles: objectsLayer },
    { name: LayerType.ENTITIES,  visible: true, locked: false, opacity: 1.0, tiles: entitiesLayer, entities: [] },
    { name: LayerType.TRIGGERS,  visible: true, locked: false, opacity: 1.0, tiles: triggersLayer },
  ],

  // ─── Collision geometry ─────────────────────────────────
  // Dual-storage: `layers` above is what the renderer paints; `platforms`
  // here is what physics uses for collision. The engine auto-generates
  // platforms from GROUND-layer tiles when this is `[]`, but an explicit
  // platform is auditable at a glance + survives tile re-authoring in the
  // editor without a hidden regen step. For a template, explicit wins.
  platforms: runtimePlatforms,

  // ─── Dynamic spawning (mobs) ────────────────────────────
  // None — this is a minimum-viable level. Add via editor or override.
  entities: [],
  spawnRules: [],

  // ─── Save behavior ──────────────────────────────────────
  // 'town' = persistent (save player position between sessions)
  // 'field' = reset (always spawn at defaultSpawn)
  type: 'town',

  // ─── Author metadata ────────────────────────────────────
  metadata: {
    author: 'DJ',
    description: 'Minimum viable map: one floor tile, one spawn point, nothing else. Canonical template for new levels as of 2026-04-17.',
    tags: ['template', 'minimal', 'reference'],
  },
};
